import React, { createContext, useContext, useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { CheckCircle, AlertCircle, Info, Shield, Sparkles, X, TrendingUp } from "lucide-react";
import { VerifiedTransaction } from "../lib/NexoraxTruthRegistry";

export interface Toast {
  id: string;
  message: string;
  type: "success" | "error" | "info" | "warning" | "transaction";
  duration?: number;
  transactionDetails?: VerifiedTransaction;
}

interface ToastContextType {
  showToast: (
    message: string,
    type?: "success" | "error" | "info" | "warning" | "transaction",
    options?: { duration?: number; transactionDetails?: VerifiedTransaction }
  ) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = (
    message: string,
    type: "success" | "error" | "info" | "warning" | "transaction" = "info",
    options?: { duration?: number; transactionDetails?: VerifiedTransaction }
  ) => {
    const id = `toast-${Math.random().toString(36).substr(2, 9)}`;
    const newToast: Toast = {
      id,
      message,
      type,
      duration: options?.duration || (type === "transaction" ? 10000 : 5000),
      transactionDetails: options?.transactionDetails,
    };
    setToasts((prev) => [...prev, newToast]);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  // Play audio pulse feedback for critical operations
  const playPulseFeedback = () => {
    try {
      if (typeof window !== "undefined") {
        const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        
        // Custom NexoraX Neural Pulse Tone (0.15s short sharp high-frequency chirp)
        const osc = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        
        osc.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        
        osc.type = "sine";
        osc.frequency.setValueAtTime(880, audioCtx.currentTime); // A5 chord
        osc.frequency.exponentialRampToValueAtTime(1760, audioCtx.currentTime + 0.15); // A6 chord (rapid rise)
        
        gainNode.gain.setValueAtTime(0.05, audioCtx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.18);
        
        osc.start();
        osc.stop(audioCtx.currentTime + 0.2);
      }
    } catch (e) {
      // AudioContext fails silently or is blocked by browser interaction policies, which is fine
    }
  };

  // Register listener for transaction logged in NexoraxTruthRegistry
  useEffect(() => {
    const handleTransactionLogged = (event: Event) => {
      const customEvent = event as CustomEvent<VerifiedTransaction>;
      if (customEvent && customEvent.detail) {
        const tx = customEvent.detail;
        
        playPulseFeedback();

        showToast(
          `VERIFIED REVENUE SECURED: £${tx.amount.toLocaleString()} via ${tx.channel}`,
          "transaction",
          {
            duration: 12000,
            transactionDetails: tx,
          }
        );
      }
    };

    const handleCustomToastTrigger = (event: Event) => {
      const customEvent = event as CustomEvent<{
        message: string;
        type?: "success" | "error" | "info" | "warning" | "transaction";
      }>;
      if (customEvent && customEvent.detail) {
        showToast(customEvent.detail.message, customEvent.detail.type || "info");
      }
    };

    window.addEventListener("nexorax-transaction-logged", handleTransactionLogged);
    window.addEventListener("nexorax-toast-trigger", handleCustomToastTrigger);
    return () => {
      window.removeEventListener("nexorax-transaction-logged", handleTransactionLogged);
      window.removeEventListener("nexorax-toast-trigger", handleCustomToastTrigger);
    };
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, removeToast }}>
      {children}
      
      {/* Toast Notification Mount Layer */}
      <div 
        id="nexorax-toast-container"
        className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3 w-full max-w-md pointer-events-none"
      >
        <AnimatePresence>
          {toasts.map((toast) => {
            const hasTx = toast.type === "transaction" && toast.transactionDetails;
            let icon = <Info className="w-5 h-5 text-cyan-400" />;
            let borderColor = "border-cyan-500/30";
            let glowColor = "shadow-[0_0_20px_rgba(6,182,212,0.15)]";
            let bgStyle = "bg-[#050b14]/95 backdrop-blur-md";

            if (toast.type === "success") {
              icon = <CheckCircle className="w-5 h-5 text-emerald-400" />;
              borderColor = "border-emerald-500/30";
              glowColor = "shadow-[0_0_20px_rgba(16,185,129,0.15)]";
            } else if (toast.type === "error") {
              icon = <AlertCircle className="w-5 h-5 text-rose-500" />;
              borderColor = "border-rose-500/30";
              glowColor = "shadow-[0_0_20px_rgba(239,68,68,0.15)]";
            } else if (toast.type === "warning") {
              icon = <AlertCircle className="w-5 h-5 text-amber-500" />;
              borderColor = "border-amber-500/30";
              glowColor = "shadow-[0_0_20px_rgba(245,158,11,0.15)]";
            } else if (toast.type === "transaction") {
              icon = <TrendingUp className="w-5 h-5 text-cyan-400 animate-pulse" />;
              borderColor = "border-cyan-500/50";
              glowColor = "shadow-[0_0_30px_rgba(6,182,212,0.25)]";
              bgStyle = "bg-[#0b101c]/95 backdrop-blur-lg";
            }

            return (
              <motion.div
                key={toast.id}
                initial={{ opacity: 0, y: 50, scale: 0.93 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, x: 100, transition: { duration: 0.2 } }}
                id={`toast-item-${toast.id}`}
                className={`p-4 rounded-2xl border ${borderColor} ${glowColor} ${bgStyle} text-white pointer-events-auto flex flex-col space-y-3 relative overflow-hidden`}
              >
                {/* 0.8s High-Velocity Loading Timeline Indicator */}
                <span className="absolute top-0 left-0 w-full h-0.5 bg-slate-900">
                  <motion.span 
                    initial={{ width: "100%" }}
                    animate={{ width: "0%" }}
                    transition={{ duration: (toast.duration || 5000) / 1000, ease: "linear" }}
                    onAnimationComplete={() => removeToast(toast.id)}
                    className={`absolute top-0 left-0 h-full ${
                      toast.type === 'transaction' ? 'bg-[#e840f3]' : 'bg-cyan-500'
                    }`}
                  />
                </span>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">{icon}</div>
                  <div className="flex-grow min-w-0 pr-4">
                    <span className="text-[9px] font-mono font-black uppercase tracking-widest text-[#e840f3] block mb-0.5">
                      {toast.type === "transaction" ? "NEXORAX REVENUE ARCHITECTURE" : "EMPIRE OS DIRECTIVE"}
                    </span>
                    <h4 className="text-xs font-sans font-extrabold text-white leading-snug">
                      {toast.message}
                    </h4>
                  </div>
                  <button 
                    onClick={() => removeToast(toast.id)}
                    className="flex-shrink-0 text-slate-500 hover:text-slate-300 transition-colors p-0.5 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Substantive Financial Architecture Decomposition for Live Verified Transactions */}
                {hasTx && (
                  <div className="border-t border-white/5 pt-2 mt-2 space-y-2 font-mono text-[9px] text-slate-400">
                    <div className="flex justify-between items-center bg-black/40 px-2 py-1.5 rounded">
                      <span>TRANSACTION CORE:</span>
                      <span className="font-extrabold text-cyan-400">{toast.transactionDetails?.id}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-1.5 bg-black/20 p-2 rounded text-slate-400">
                      <div>
                        <span className="text-[8px] text-slate-600 block">CLIENT HUB ID</span>
                        <span className="font-black text-rose-300 block truncate">{toast.transactionDetails?.clientName}</span>
                      </div>
                      <div>
                        <span className="text-[8px] text-slate-600 block">TIERED INTERPRET CHANNEL</span>
                        <span className="font-black text-white block">{toast.transactionDetails?.channel?.toUpperCase()}</span>
                      </div>
                    </div>

                    {/* Tiered Extraction Protocol Computations */}
                    <div className="border-t border-dashed border-white/5 pt-2 space-y-1 bg-neutral-950/40 p-2.5 rounded-xl">
                      <span className="text-[8px] text-[#e840f3] font-black uppercase tracking-widest block mb-1">
                        TIERED EXTRACTION PROTOCOL:
                      </span>
                      
                      {(() => {
                        const baseAmount = toast.transactionDetails?.amount || 0;
                        const levy = baseAmount * 0.01;
                        const remaining = baseAmount - levy;
                        const tax = remaining * 0.20;
                        const net = remaining - tax;
                        
                        return (
                          <div className="space-y-1">
                            <div className="flex justify-between">
                              <span className="text-zinc-600">1% 3% Community Allocation (ZAKAT split 1/3)</span>
                              <span className="font-bold text-[#e840f3]">£{levy.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-zinc-600">10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee)</span>
                              <span className="font-bold text-amber-500">£{tax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between border-t border-white/5 pt-1 mt-1 text-white font-black">
                              <span className="text-cyan-400">NET SETTLEMENT TO Secure Payment Provider</span>
                              <span className="text-cyan-400">£{net.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    <span className="text-[7.5px] text-slate-600 uppercase tracking-wide block text-center mt-1">
                      REAL-TIME LEDGER MIRRORED VIA CloudSync-02 // SecurePayment ACC: 04-**-** | ******15
                    </span>
                  </div>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
